import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

/**
 * AuthGuard Component
 * Hiển thị overlay với icon khóa và bắt đăng nhập khi chưa authenticate
 *
 * @param {boolean} requireAuth - Có cần đăng nhập không
 * @param {React.ReactNode} children - Nội dung bên trong
 * @param {string} action - Tên hành động (để hiển thị trong modal)
 */
const AuthGuard = ({
  requireAuth = true,
  children,
  action = "thực hiện hành động này",
  showLock = true,
  onAuthRequired,
}) => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  // Nếu không yêu cầu auth hoặc đã đăng nhập -> render bình thường
  if (!requireAuth || isLoggedIn) {
    return <>{children}</>;
  }

  // Xử lý khi click vào locked element
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (onAuthRequired) {
      onAuthRequired();
    } else {
      setShowModal(true);
    }
  };

  const handleLogin = () => {
    setShowModal(false);
    navigate("/login", { state: { from: window.location.pathname } });
  };

  const handleRegister = () => {
    setShowModal(false);
    navigate("/register", { state: { from: window.location.pathname } });
  };

  return (
    <>
      {/* Wrapper với overlay */}
      <div className="auth-guard-wrapper" onClick={handleClick}>
        {/* Content bị mờ */}
        <div className="auth-guard-content">{children}</div>

        {/* Overlay với icon khóa */}
        {showLock && (
          <div className="auth-guard-overlay">
            <div className="auth-guard-lock-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-12 h-12"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
              <p className="auth-guard-message">Đăng nhập để {action}</p>
            </div>
          </div>
        )}
      </div>

      {/* Modal đăng nhập */}
      {showModal && (
        <div
          className="auth-modal-backdrop"
          onClick={() => setShowModal(false)}
        >
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="auth-modal-close"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>

            <div className="auth-modal-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-16 h-16"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
            </div>

            <h2 className="auth-modal-title">Yêu cầu đăng nhập</h2>
            <p className="auth-modal-description">
              Bạn cần đăng nhập để {action}. Vui lòng đăng nhập hoặc tạo tài
              khoản mới.
            </p>

            <div className="auth-modal-actions">
              <button className="auth-modal-btn primary" onClick={handleLogin}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
                  />
                </svg>
                Đăng nhập
              </button>
              <button
                className="auth-modal-btn secondary"
                onClick={handleRegister}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
                  />
                </svg>
                Đăng ký ngay
              </button>
            </div>

            <p className="auth-modal-footer">
              Bằng việc đăng nhập, bạn đồng ý với{" "}
              <a href="/terms" target="_blank">
                Điều khoản sử dụng
              </a>{" "}
              và{" "}
              <a href="/privacy" target="_blank">
                Chính sách bảo mật
              </a>{" "}
              của chúng tôi.
            </p>
          </div>
        </div>
      )}

      <style jsx>{`
        /* Wrapper */
        .auth-guard-wrapper {
          position: relative;
          display: inline-block;
          width: 100%;
          cursor: not-allowed;
        }

        /* Content bị mờ */
        .auth-guard-content {
          filter: blur(2px);
          opacity: 0.6;
          pointer-events: none;
          user-select: none;
        }

        /* Overlay */
        .auth-guard-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          transition: all 0.3s ease;
        }

        .auth-guard-overlay:hover {
          background: rgba(15, 23, 42, 0.5);
        }

        /* Lock Icon */
        .auth-guard-lock-icon {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          color: #4ade80;
          animation: fadeIn 0.3s ease;
        }

        .auth-guard-lock-icon svg {
          filter: drop-shadow(0 4px 12px rgba(74, 222, 128, 0.4));
          animation: pulse 2s infinite;
        }

        .auth-guard-message {
          font-size: 14px;
          font-weight: 600;
          color: #e5e7eb;
          text-align: center;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
          margin: 0;
        }

        /* Modal Backdrop */
        .auth-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.2s ease;
        }

        /* Modal */
        .auth-modal {
          background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
          border: 1px solid rgba(74, 222, 128, 0.3);
          border-radius: 16px;
          padding: 32px;
          max-width: 440px;
          width: 90%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          position: relative;
          animation: slideUp 0.3s ease;
        }

        .auth-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: transparent;
          border: none;
          color: #94a3b8;
          font-size: 32px;
          cursor: pointer;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .auth-modal-close:hover {
          background: rgba(74, 222, 128, 0.1);
          color: #4ade80;
        }

        .auth-modal-icon {
          display: flex;
          justify-content: center;
          margin-bottom: 20px;
          color: #4ade80;
        }

        .auth-modal-icon svg {
          filter: drop-shadow(0 4px 12px rgba(74, 222, 128, 0.3));
        }

        .auth-modal-title {
          font-size: 24px;
          font-weight: 700;
          color: #f1f5f9;
          text-align: center;
          margin: 0 0 12px 0;
        }

        .auth-modal-description {
          font-size: 15px;
          color: #cbd5e1;
          text-align: center;
          line-height: 1.6;
          margin: 0 0 28px 0;
        }

        /* Actions */
        .auth-modal-actions {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }

        .auth-modal-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 24px;
          border: none;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .auth-modal-btn.primary {
          background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
          color: #0f172a;
          box-shadow: 0 4px 12px rgba(74, 222, 128, 0.3);
        }

        .auth-modal-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(74, 222, 128, 0.4);
        }

        .auth-modal-btn.secondary {
          background: rgba(74, 222, 128, 0.1);
          color: #4ade80;
          border: 1px solid rgba(74, 222, 128, 0.3);
        }

        .auth-modal-btn.secondary:hover {
          background: rgba(74, 222, 128, 0.2);
          border-color: #4ade80;
        }

        /* Footer */
        .auth-modal-footer {
          font-size: 12px;
          color: #94a3b8;
          text-align: center;
          line-height: 1.5;
          margin: 0;
        }

        .auth-modal-footer a {
          color: #4ade80;
          text-decoration: none;
        }

        .auth-modal-footer a:hover {
          text-decoration: underline;
        }

        /* Animations */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .auth-modal {
            padding: 24px;
            max-width: 90%;
          }

          .auth-modal-title {
            font-size: 20px;
          }

          .auth-modal-description {
            font-size: 14px;
          }

          .auth-guard-lock-icon svg {
            width: 48px;
            height: 48px;
          }

          .auth-guard-message {
            font-size: 12px;
          }
        }
      `}</style>
    </>
  );
};

export default AuthGuard;
